import { CssVarsThemeOptions, extendTheme } from '@mui/joy/styles';
import { Theme } from '@mui/joy/styles/types/theme';
import { Components, StyleOverrides } from '@mui/joy/styles/components';
import { AccordionOwnerState, AccordionSlot } from '@mui/joy/Accordion/AccordionProps';
import { DrawerProps } from '@mui/joy/Drawer/DrawerProps';
import React from 'react';
import { Chip, IconButton } from '@mui/joy';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const FONT_FAMILY_DISPLAY = ['podium-sharp-variable', 'sans-serif'].join(', ');
const FONT_FAMILY_BODY = ['American Typewriter'].join(', ');

export const theme = extendTheme({
	components: {
		JoyDialogTitle: {
			styleOverrides: {
				root: ({ ownerState, theme }) => ({
					color: theme.palette.text.primary,
				}),
			},
		},
		JoyDialogContent: {
			styleOverrides: {
				root: () => ({
					gap: theme.spacing(1),
					[theme.breakpoints.up('md')]: {
						gap: theme.spacing(2),
					},
				}),
			},
		},
		JoyDrawer: {
			styleOverrides: {
				root: ({ ownerState, theme }: any) => ({
					...(ownerState.size === 'xl' &&
						(ownerState.anchor === 'top' || ownerState.anchor === 'bottom') && {
							'--Drawer-verticalSize': 'clamp(450px, 90%, 100%)',
						}),

					...(ownerState.size === 'xl' &&
						(ownerState.anchor === 'left' || ownerState.anchor === 'right') && {
							'--Drawer-horizontalSize': 'clamp(500px, 90%, 100%)',
						}),

					...(ownerState.size === 'md' && {
						'--Drawer-horizontalSize': 'clamp(500px, 30%, 100%)',
					}),
				}),
			},
		},
		JoyCard: {
			styleOverrides: {
				root: {
					border: 'none',
				},
			},
		},
		JoyIconButton: {
			styleOverrides: {
				root: ({ ownerState, theme }) => ({
					...(ownerState.variant === 'plain' && {
						color: theme.palette.text.icon,
					}),
				}),
			},
		},
		JoyButton: {
			styleOverrides: {
				root: ({ ownerState, theme }) => ({
					borderRadius: '0px',
					fontSize: '1rem',
					fontWeight: 300,

					...(ownerState.variant === 'solid' && {
						borderBottom: '2px solid',
						borderColor: theme.vars.palette.danger[900],
					}),
					...(ownerState.variant === 'soft' && {
						borderBottom: '2px solid',

						borderColor: theme.vars.palette.success[900],
						...(ownerState.color === 'success' && {
							color: theme.palette.common.white,
							backgroundColor: theme.vars.palette.success[600],
							'&:hover': {
								backgroundColor: theme.vars.palette.success[800],
								color: theme.palette.common.white,
							},
						}),
					}),
					...(ownerState.variant === 'plain' && {
						'&:hover': {
							backgroundColor: 'transparent',
							textDecoration: 'underline',
						},
					}),
				}),
			},
		},
		JoyLink: {
			styleOverrides: {
				root: ({ ownerState, theme }) => ({
					textDecoration: 'underline',
					'&:hover': {
						textDecoration: 'none',
					},
				}),
			},
		},
		JoyInput: {
			defaultProps: {
				variant: 'outlined',
				color: 'neutral',
			},
			styleOverrides: {
				root: ({ ownerState, theme }) => ({
					padding: '0px 12px',
					borderRadius: '0px',
					boxShadow: 'none',
					color: theme.vars.palette.text.primary,

					border: '2px solid',
					borderColor: theme.palette.common.black,
					backgroundColor: 'transparent',
				}),
			},
		},
		JoyTextarea: {
			defaultProps: {
				variant: 'outlined',
				color: 'neutral',
			},
			styleOverrides: {
				root: ({ ownerState, theme }) => ({
					padding: '12px 12px',
					borderRadius: '0px',
					boxShadow: 'none',
					color: theme.vars.palette.text.primary,

					border: '2px solid',
					borderColor: theme.palette.common.black,
					backgroundColor: 'transparent',
				}),
			},
		},
		JoySelect: {
			defaultProps: {
				variant: 'outlined',
				color: 'neutral',
			},
			styleOverrides: {
				root: ({ ownerState, theme }) => ({
					padding: '0px 12px',
					borderRadius: '0px',
					boxShadow: 'none',
					color: theme.vars.palette.text.primary,

					border: '2px solid',
					borderColor: theme.palette.common.black,
					backgroundColor: 'transparent',
					...(ownerState.variant === 'soft' && {
						border: 'none',
					}),
				}),
			},
		},

		JoyListItemButton: {
			styleOverrides: {
				root: ({ ownerState, theme }) => ({
					borderRadius: 0,
				}),
			},
		},
	},

	typography: {
		fontFamily: {
			display: FONT_FAMILY_DISPLAY,
			body: FONT_FAMILY_BODY,
		},
		'body-sm': {
			color: 'var(--mod-palette-text-primary, #464443)',
		},
	},

	colorSchemes: {
		light: {
			palette: {
				primary: {
					50: '#359efa',
					100: '#288de6',
					200: '#2082d7',
					300: '#1f7dce',
					400: '#1d75c1',
					500: '#1d70bb',
					600: '#1a6ab0',
					700: '#1760a1',
					800: '#14558d',
					900: '#10487a',
				},
				success: {
					50: '#d0f0e4',
					100: '#a0e0c8',
					200: '#70d0ac',
					300: '#40c090',
					400: '#20b074',
					500: '#00a058',
					600: '#005A30',
					700: '#008044',
					800: '#006f3a',
					900: '#002D18',
				},
				neutral: {
					500: '#F3F2F1',
				},
				secondary: {
					main: '#d4351c', // UK government secondary color
					contrastText: '#ffffff',
				},
				text: {
					primary: '#121212', // UK government primary text color
					secondary: '#505a5f', // UK government secondary text color
					tertiary: '#ffffff', // UK government tertiary text color
					disabled: '#999999', // UK government disabled text color
				},
				divider: '#B1B4B6',

				background: {
					default: '#ffffff', // UK government default background color
					paper: '#f9f9f9', // UK government paper background color
					backdrop: '#F3F2F1',
				},
			},
		},
	},

	shape: {
		borderRadius: 4, // UK government default border radius
	},

	shadows: [
		'none',
		'0px 2px 4px rgba(0, 0, 0, 0.05)', // UK government shadow style
		'0px 4px 8px rgba(0, 0, 0, 0.1)', // UK government shadow style
		'0px 8px 16px rgba(0, 0, 0, 0.1)', // UK government shadow style
	],

	breakpoints: {
		values: {
			xs: 0,
			sm: 600,
			md: 900,
			lg: 1200,
			xl: 1500,
			xxl: 1800,
		},
	},
} as CssVarsThemeOptions & {
	components?: Components<Theme> & {
		JoyDrawer?: {
			defaultProps?: Partial<DrawerProps>;
			styleOverrides?: StyleOverrides<AccordionSlot, AccordionOwnerState, Theme>;
		};
	};
});
